/** @jsx jsx */
import { Box, Button, CompanyAvatar, Flex, Heading, Inline, Paragraph, Stack, WysiwygViewer } from "@eworkit/iris";
import { PublicProfile, NewsItem } from "data/public-profiles";
import { htmlToText } from "html-to-text";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { ResponsiveCard } from "@/components/ResponsiveCard";
import { ShareIcons } from "@/components/Share";
import { agoDate } from "@/core/dates";
import { router } from "@/views/router";

import { PublicProfileNewsCoverImage } from "./PublicProfileNewsCoverImage";

interface PublicProfileNewsCardProps {
  publicProfile: PublicProfile;
  news: NewsItem;
  isPublic?: boolean;
  full?: boolean;
}

export const PublicProfileNewsCard: FC<PublicProfileNewsCardProps> = ({ publicProfile, news, isPublic, full }) => {
  const { t } = useTranslation("public");

  const baseRoute = isPublic ? "public-client" : "client";
  const detailsPath = router.get(`${baseRoute}.news-detail`, { slug: publicProfile.slug, newsId: news.id }).path;
  const publicUrl =
    window.location.origin +
    router.get("public-client.news-detail", { slug: publicProfile.slug, newsId: news.id }).path;

  return (
    <ResponsiveCard sx={{ py: "md" }}>
      <Stack gap="md">
        <Stack gap="sm">
          <Inline gap="xs">
            <CompanyAvatar size="md" img={publicProfile.logo.publicUrl ?? undefined} name={publicProfile.name} />

            <Stack gap="xxs">
              <Paragraph ellipsis>{publicProfile.name}</Paragraph>
              <Paragraph size="p-sm" color="n700">
                {agoDate(news.publicationDate)}
              </Paragraph>
            </Stack>
          </Inline>

          <PublicProfileNewsCoverImage image={news.coverImage} />
        </Stack>

        <Stack gap="sm">
          <Heading size="h-md">{news.title}</Heading>

          {full ? (
            <WysiwygViewer value={news.longText} />
          ) : (
            <Paragraph
              size="p-lg"
              sx={{
                lineClamp: 3,
                WebkitLineClamp: 3,
                display: "-webkit-box",
                overflow: "hidden",
                textOverflow: "ellipsis",
                WebkitBoxOrient: "vertical",
              }}>
              {news.shortText?.length ? news.shortText : htmlToText(news.longText)}
            </Paragraph>
          )}

          <Flex alignItems="center" justifyContent="space-between">
            <Box>
              {!full && (
                <Button as={Link} to={detailsPath} variant="secondary" size="sm">
                  {t("CLIENT_PAGE.NEWS.BUTTON_READ_MORE")}
                </Button>
              )}
            </Box>

            <Flex gap="xs" alignItems="center">
              <Paragraph sx={{ visibility: ["hidden", null, null, "visible"] }} size="p-md" color="n700">
                {t("CLIENT_PAGE.NEWS.SHARE")}
              </Paragraph>
              <ShareIcons url={publicUrl} size="sm" />
            </Flex>
          </Flex>
        </Stack>
      </Stack>
    </ResponsiveCard>
  );
};
